import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { LocalStorageService } from "../services/LocalStorage";

export enum FeatureFlag {
  // ***     Add feature flags here     ***
  // (They should always be lower case)
  NewFieldInfo = "newfieldinfo",
  BoundaryExportMove = "boundaryexportmove", // Boundary export move to interactive map
  FieldEditUpdate = "fieldeditupdate", // Limit field editing by role and enrollment
  SatelliteImageryData = "satelliteimagerydata",
  SatelliteBaselineData = "satellitebaselinedata",
  SatelliteAnomalyData = "satelliteanomalydata",
  SatelliteMSAVIData = "satellitemsavidata",
  SatelliteSecondaryLayers = "satellitesecondarylayers",
}

export const FeatureFlagList: string[] = Object.values(FeatureFlag);

const ENV_FEATURE_FLAGS_RAW: string = import.meta.env.VITE_FEATURE_FLAGS || "";
const ENV_FEATURE_FLAGS = ENV_FEATURE_FLAGS_RAW.toLowerCase().split(",");

export const isDeployFeatureFlagOn = (featureFlag: FeatureFlag) => {
  return ENV_FEATURE_FLAGS.includes(featureFlag);
};

/**
 * Detect if a feature flag is active (either via environment variable or local storage)
 * Local storage takes precedence over environment variables.
 *
 * @param featureFlag the string name of the desired feature flag
 * @returns Whether the feature flag is active or not
 */
export const isFeatureFlagEnabled = (featureFlag: FeatureFlag): boolean => {
  const localStorageFeatureFlag = LocalStorageService.getItem(featureFlag);

  const envFeatureFlag = isDeployFeatureFlagOn(featureFlag);

  // If the feature flag is specifically set to false in local storage, return false
  if (localStorageFeatureFlag === "false") {
    return false;
  }

  return localStorageFeatureFlag === "true" || envFeatureFlag;
};

/**
 * List of constants for tracking feature flag status throughout the code. This is a
 * centralized call to isFeatureFlagEnabled for each flag. Delete these constants when
 * deleting the feature flag.
 */
export const isNewFieldInfo = isFeatureFlagEnabled(FeatureFlag.NewFieldInfo);
export const isBoundaryExportMove = isFeatureFlagEnabled(
  FeatureFlag.BoundaryExportMove,
);
export const isFieldEditUpdate = isFeatureFlagEnabled(
  FeatureFlag.FieldEditUpdate,
);
export const isSatelliteImageryEnabled = isFeatureFlagEnabled(
  FeatureFlag.SatelliteImageryData,
);
export const isSatelliteBaselineDataEnabled = isFeatureFlagEnabled(
  FeatureFlag.SatelliteBaselineData,
);
export const isSatelliteAnomalyDataEnabled = isFeatureFlagEnabled(
  FeatureFlag.SatelliteAnomalyData,
);
export const isSatelliteMSAVIDataEnabled = isFeatureFlagEnabled(
  FeatureFlag.SatelliteMSAVIData,
);
export const isSatelliteSecondaryLayersEnabled = isFeatureFlagEnabled(
  FeatureFlag.SatelliteSecondaryLayers,
);

/**
 * Hook to add any feature flags from the URL to local storage, so that it can
 * be tracked in between page loads and navigation.
 */
export const useStoreFeatureFlags = () => {
  const [searchParams] = useSearchParams();

  // if any feature flags exist in search params, add them to the local storage
  useEffect(() => {
    for (const flag of FeatureFlagList) {
      if (searchParams.has(flag)) {
        if (searchParams.get(flag) === "false") {
          LocalStorageService.setItem(flag, "false");
        } else {
          LocalStorageService.setItem(flag, "true");
        }
      }
    }
  }, [searchParams]);
};

export const generateUrlWithCurrentFlags = (
  targetUrl: string,
  existingSearchParams: URLSearchParams,
): URLSearchParams => {
  // Parse the target URL
  const searchParams = new URLSearchParams(targetUrl);
  // For every feature flag in the current url, add a value to the new search params
  for (const key of existingSearchParams.keys()) {
    if (FeatureFlagList.includes(key)) {
      searchParams.append(key, "");
    }
  }
  return searchParams;
};
